import type { EChartsOption } from 'echarts'
import { axisTextStyle, formatCompactNumber, isFiniteNumber, richSafe, tooltipBase, tooltipHtml, type TooltipRow } from './format'
import { seriesColor, withAlpha, type ChartTheme } from './palette'

/** Точка рассеяния: объект (кампания, артикул) с двумя мерами и, по желанию, третьей — размером. */
export interface ScatterPoint {
  key: string
  label: string
  sublabel?: string | null
  /** `null` — точка не рисуется (нет данных по оси), но остаётся в табличном виде страницы. */
  x: number | null
  y: number | null
  /** Третья мера — площадь точки (заказы, выручка); без неё все точки одного размера. */
  size?: number | null
  /** Ключ группы цвета (`ScatterGroup.key`). */
  group?: string | null
}

/** Группа цвета. Не больше трёх: больше цветов на рассеянии не различить, четвёртая и далее — «Прочее». */
export interface ScatterGroup {
  key: string
  label: string
  /** Слот категориальной палитры (цвет закреплён за сущностью). */
  slot?: number
  color?: string
}

export interface ScatterThreshold {
  value: number
  label: string
}

export interface ScatterInput {
  points: ScatterPoint[]
  groups?: ScatterGroup[]
  xLabel: string
  yLabel: string
  xFormat?: (value: number) => string
  yFormat?: (value: number) => string
  /** Короткие подписи осей. */
  xAxisFormat?: (value: number) => string
  yAxisFormat?: (value: number) => string
  sizeLabel?: string
  sizeFormat?: (value: number) => string
  /** Логарифмическая ось X: расход кампаний различается на порядки. Точки с x ≤ 0 тогда не рисуются. */
  xLog?: boolean
  /** Порог по Y (допустимый ДРР) — горизонтальная отметка с подписью. */
  thresholdY?: ScatterThreshold | null
  /** Порог по X. */
  thresholdX?: ScatterThreshold | null
}

export const SCATTER_MAX_GROUPS = 3
export const SCATTER_OTHER_GROUP = '__other__'
const MIN_SYMBOL = 8
const MAX_SYMBOL = 26

/** Цвет группы: первые три — свои слоты, остальные — «Прочее». */
export function scatterGroupColor(theme: ChartTheme, groups: ScatterGroup[], key: string | null | undefined): string {
  const index = groups.findIndex((group) => group.key === key)
  if (index < 0 || index >= SCATTER_MAX_GROUPS) return theme.other
  const group = groups[index]
  return group.color ?? seriesColor(theme, group.slot ?? index)
}

/** Размер точки: площадь пропорциональна мере (корень), от 8 до 26 px; без меры — 10 px. */
export function scatterSymbolSize(size: number | null | undefined, maxSize: number): number {
  if (!isFiniteNumber(size) || size <= 0 || maxSize <= 0) return 10
  return Math.round(MIN_SYMBOL + (MAX_SYMBOL - MIN_SYMBOL) * Math.sqrt(size / maxSize))
}

/** Точки, которые можно нарисовать: обе координаты есть, а на логарифмической оси x > 0. */
export function plottablePoints(input: Pick<ScatterInput, 'points' | 'xLog'>): ScatterPoint[] {
  return input.points.filter(
    (point) => isFiniteNumber(point.x) && isFiniteNumber(point.y) && (!input.xLog || (point.x as number) > 0),
  )
}

function thresholdLine(theme: ChartTheme, threshold: ScatterThreshold, axis: 'x' | 'y') {
  return {
    silent: true,
    symbol: ['none', 'none'],
    animation: false,
    lineStyle: { color: theme.textMuted, width: 1, type: 'dashed', opacity: 0.9 },
    label: {
      show: true,
      position: axis === 'y' ? 'insideEndTop' : 'insideEndTop',
      color: theme.textSecondary,
      fontSize: 11,
      fontFamily: theme.fontFamily,
      formatter: () => richSafe(threshold.label),
    },
    data: [axis === 'y' ? { yAxis: threshold.value } : { xAxis: threshold.value }],
  }
}

/**
 * Рассеяние «мера × мера» (расход × ДРР по кампаниям): одна ось на меру, точки с кольцом цвета поверхности,
 * не больше трёх цветов групп, пороги — пунктирной отметкой с подписью. Подсказка на точке: название, обе меры и
 * размер. Подписи — цветом текста DS.
 */
export function buildScatterOption(input: ScatterInput, theme: ChartTheme): EChartsOption {
  const groups = input.groups ?? []
  const xFormat = input.xFormat ?? ((value: number) => formatCompactNumber(value))
  const yFormat = input.yFormat ?? ((value: number) => formatCompactNumber(value))
  const xAxisFormat = input.xAxisFormat ?? xFormat
  const yAxisFormat = input.yAxisFormat ?? yFormat
  const points = plottablePoints(input)
  const maxSize = Math.max(0, ...points.map((point) => (isFiniteNumber(point.size) ? (point.size as number) : 0)))

  // Ряды по группам в порядке `groups`; точки без группы и «лишние» группы — в общий ряд «Прочее».
  const shownGroups = groups.slice(0, SCATTER_MAX_GROUPS)
  const seriesKeys = [...shownGroups.map((group) => group.key), SCATTER_OTHER_GROUP]
  const byKey = new Map<string, Array<{ value: [number, number]; index: number; symbolSize: number }>>()
  for (const key of seriesKeys) byKey.set(key, [])
  input.points.forEach((point, index) => {
    if (!points.includes(point)) return
    const key = shownGroups.some((group) => group.key === point.group) ? (point.group as string) : SCATTER_OTHER_GROUP
    byKey.get(key)!.push({ value: [point.x as number, point.y as number], index, symbolSize: scatterSymbolSize(point.size, maxSize) })
  })

  const series = seriesKeys
    .filter((key) => (byKey.get(key) ?? []).length > 0 || key !== SCATTER_OTHER_GROUP)
    .map((key, seriesIndex) => {
      const group = shownGroups.find((item) => item.key === key)
      const color = key === SCATTER_OTHER_GROUP ? theme.other : scatterGroupColor(theme, groups, key)
      return {
        type: 'scatter' as const,
        id: key,
        name: group?.label ?? 'Прочее',
        cursor: 'pointer',
        data: (byKey.get(key) ?? []).map((item) => ({ value: item.value, symbolSize: item.symbolSize, pointIndex: item.index })),
        itemStyle: { color: withAlpha(color, 0.85), borderColor: theme.surface, borderWidth: 2 },
        emphasis: { scale: 1.4, itemStyle: { color, borderColor: theme.text, borderWidth: 1 } },
        ...(seriesIndex === 0 && input.thresholdY ? { markLine: thresholdLine(theme, input.thresholdY, 'y') } : {}),
        ...(seriesIndex === 0 && input.thresholdX && !input.thresholdY ? { markLine: thresholdLine(theme, input.thresholdX, 'x') } : {}),
      }
    })

  return {
    backgroundColor: 'transparent',
    animation: false,
    textStyle: { fontFamily: theme.fontFamily },
    aria: { enabled: true },
    grid: { top: 28, left: 8, right: 24, bottom: 8, outerBoundsMode: 'same', outerBoundsContain: 'axisLabel' } as any,
    legend: { show: false },
    tooltip: {
      ...tooltipBase(theme),
      trigger: 'item',
      formatter: (params: any) => {
        const point = input.points[params?.data?.pointIndex]
        if (!point) return ''
        const rows: TooltipRow[] = [
          { label: input.xLabel, value: isFiniteNumber(point.x) ? xFormat(point.x) : '—' },
          { label: input.yLabel, value: isFiniteNumber(point.y) ? yFormat(point.y) : '—' },
        ]
        if (input.sizeLabel) {
          rows.push({
            label: input.sizeLabel,
            value: isFiniteNumber(point.size) ? (input.sizeFormat ?? formatCompactNumber)(point.size) : '—',
          })
        }
        const group = groups.find((item) => item.key === point.group)
        const footer = [point.sublabel ?? '', group ? group.label : ''].filter(Boolean)
        return tooltipHtml(theme, point.label, rows, footer.length ? [footer.join(' · ')] : undefined)
      },
    },
    xAxis: {
      type: input.xLog ? 'log' : 'value',
      name: input.xLabel,
      nameLocation: 'middle',
      nameGap: 28,
      nameTextStyle: { ...axisTextStyle(theme) },
      axisLine: { show: true, lineStyle: { color: theme.axis } },
      axisTick: { show: false },
      axisLabel: { ...axisTextStyle(theme), formatter: (value: number) => xAxisFormat(value) },
      splitLine: { lineStyle: { color: theme.grid, width: 1, type: 'solid' } },
      scale: !input.xLog,
    },
    yAxis: {
      type: 'value',
      name: input.yLabel,
      nameTextStyle: { ...axisTextStyle(theme), align: 'left' },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { ...axisTextStyle(theme), formatter: (value: number) => yAxisFormat(value) },
      splitLine: { lineStyle: { color: theme.grid, width: 1, type: 'solid' } },
      min: points.every((point) => (point.y as number) >= 0) ? 0 : undefined,
      max: input.thresholdY ? (value: { max: number }) => Math.max(value.max, input.thresholdY!.value * 1.1) : undefined,
    },
    series,
  } as EChartsOption
}
